import { useState, useCallback } from 'react';
import { supabase } from '../config/supabaseClient';

// Types for diagnostic conversation
export interface DiagnosticConversationResponse {
  question?: string;
  question_type?: string;
  purpose?: string;
  conversation_stage: number;
  conversation_complete: boolean;
  detailed_problem_description?: string;
  next_action?: string;
  quick_answers?: string[];
  error?: string;
}

export interface CompleteDiagnosticResponse {
  conversation_stage: string; // "questioning" | "quote_ready" | "complete"
  conversation_id?: string;
  quote_id?: number;
  make?: string;
  model?: string;
  conversation?: DiagnosticConversationResponse;
  error?: string;
  processing_status: string; // "in_progress" | "complete" | "error"
}

export interface DiagnosticMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
}

interface UseDiagnosticConversationReturn {
  messages: DiagnosticMessage[];
  conversationId: string | null;
  quoteId: number | null;
  stage: string;
  loading: boolean;
  error: string | null;
  isConversationComplete: boolean;
  quickAnswers: string[] | null;
  startConversation: (make: string, model: string) => Promise<boolean>;
  continueConversation: (answer: string) => Promise<boolean>;
  clearError: () => void;
}

// Edge function name for public diagnostics
const EDGE_FUNCTION_NAME = 'public-diagnostic';

export const useDiagnosticConversation = (organizationId: string, customerId?: string | null, machineId?: number | null): UseDiagnosticConversationReturn => {
  const [messages, setMessages] = useState<DiagnosticMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [stage, setStage] = useState<string>('initial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConversationComplete, setIsConversationComplete] = useState(false);
  const [quickAnswers, setQuickAnswers] = useState<string[] | null>(null);

  const invokeEdgeFunction = useCallback(async (body: any) => {
    const requestBody = {
      ...body,
      organizationId, // Include org ID in body
      ...(customerId && { customerId }) // Include customer ID if available
    };

    // console.log('Diagnostic request body:', requestBody);

    const { data, error } = await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: requestBody
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(error.message || 'Edge function invocation failed');
    }

    if (data?.error) {
      console.error('Edge function returned error:', data.error);
      throw new Error(data.error);
    }

    // console.log('Diagnostic response:', data);
    return data;
  }, [organizationId, customerId]);

  const startConversation = useCallback(async (
    make: string,
    model: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setQuickAnswers(null);

    try {
      const initialProblem = `Customer wants diagnostic help with their ${make} ${model}`;

      const response: CompleteDiagnosticResponse = await invokeEdgeFunction({
        action: 'start',
        initial_problem: initialProblem,
        make,
        model,
        ...(machineId != null && { machineId })
      });

      if (!response.conversation_id) {
        throw new Error('Backend did not return conversation_id');
      }

      setConversationId(response.conversation_id);
      setStage(response.conversation_stage);

      // Add first AI message
      if (response.conversation?.question) {
        const aiMessage: DiagnosticMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: response.conversation.question,
          timestamp: new Date()
        };
        setMessages([aiMessage]);
      }

      return true;
    } catch (err: any) {
      console.error('Error starting diagnostic conversation:', err);
      setError(err?.message || 'Failed to start diagnostic conversation');
      return false;
    } finally {
      setLoading(false);
    }
  }, [invokeEdgeFunction, machineId]);

  const continueConversation = useCallback(async (
    answer: string
  ): Promise<boolean> => {
    if (!conversationId) {
      setError('No active conversation');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Add user message to state
      const userMessage: DiagnosticMessage = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: answer,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);

      const response: CompleteDiagnosticResponse = await invokeEdgeFunction({
        action: 'continue',
        conversationId,
        customer_answer: answer
      });

      setStage(response.conversation_stage);

      // Store quote_id if available
      if (response.quote_id) {
        // console.log('Quote ID received:', response.quote_id);
        setQuoteId(response.quote_id);
      } else {
        console.warn('No quote_id in response, stage:', response.conversation_stage);
      }

      // Check if conversation is complete
      // Complete when problem narrowing is done (conversation_complete stage)
      // or when we have a quote ready, or when fully complete
      const isComplete =
        response.conversation_stage === 'conversation_complete' ||
        response.conversation_stage === 'quote_ready' ||
        response.conversation_stage === 'complete' ||
        response.conversation?.conversation_complete === true;

      if (isComplete) {
        setIsConversationComplete(true);
        setQuickAnswers(null);

        // Add completion message
        const completionMessage: DiagnosticMessage = {
          id: `msg-${Date.now()}-complete`,
          role: 'assistant',
          content: "Thank you! You can continue with your request.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, completionMessage]);
      } else if (response.conversation?.question) {
        // Add next AI question
        const aiMessage: DiagnosticMessage = {
          id: `msg-${Date.now()}-ai`,
          role: 'assistant',
          content: response.conversation.question,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
        setQuickAnswers(response.conversation.quick_answers?.length ? response.conversation.quick_answers : null);
      }

      return true;
    } catch (err: any) {
      console.error('Error continuing conversation:', err);
      setError(err?.message || 'Failed to continue conversation');
      return false;
    } finally {
      setLoading(false);
    }
  }, [conversationId, invokeEdgeFunction]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    conversationId,
    quoteId,
    stage,
    loading,
    error,
    isConversationComplete,
    quickAnswers,
    startConversation,
    continueConversation,
    clearError,
  };
};
